<template>
  <HgModal v-model="internalModel" @close="onClose(currentUser)">
    <template #title>{{
      t(`agency.userManagement.modal.${getFlavour(currentUser)}.title`)
    }}</template>
    <template #default>
      <div>
        <p>
          {{ t(`agency.userManagement.modal.${getFlavour(currentUser)}.text`) }}
        </p>
        <CommonHint v-if="!currentUser.requiredMFA" :hasSlot="true" hintText="" icon="Warning">
          {{ t(`agency.userManagement.modal.${getFlavour(currentUser)}.hint`) }}
        </CommonHint>
      </div>
    </template>
    <template #footer>
      <HgButton
        data-cy="modal-mfa-close-btn"
        :disabled="isLoading"
        theme="primary-bordered"
        @click="onClose(currentUser)"
      >
        {{ t(`agency.userManagement.modal.${getFlavour(currentUser)}.button.cancel`) }}
      </HgButton>
      <HgButton data-cy="modal-mfa-action-btn" @click="onActionButton(currentUser)">{{
        t(`agency.userManagement.modal.${getFlavour(currentUser)}.button.action`)
      }}</HgButton>
    </template>
  </HgModal>
</template>

<script lang="ts" setup>
import { HgButton, HgModal } from '@smg-real-estate/domus';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

import { sendMFAEnrollmentEmail, toggleMfaRequirement } from '@/api/user';
import type { UserRow } from '@/components/Agency/UserFormData/types';
import CommonHint from '@/components/Common/CommonHint/CommonHint.vue';
import { ModalIds, type Modal } from '@/components/ModalManager/types';
import { ToastTheme } from '@/store/app/AppStore';
import { useAgencyStore } from '@/stores/agency/useAgencyStore';
import { useUserStore } from '@/stores/user/useUserStore';

interface Props {
  modelValue: boolean;
}
const props = defineProps<Props>();

const emit = defineEmits(['update:modelValue', 'onClose']);

const store = useStore();
const userStore = useUserStore();
const agencyStore = useAgencyStore();
const { t } = useI18n();
const isLoading = ref<boolean>(false);
const hasError = ref<boolean>(false);
const isSuccessful = ref<boolean>(false);

const currentUser = computed<UserRow>(() => {
  const mfaModal = store.getters.getModals.find(
    (modal: Modal) => modal.id === ModalIds.MfaConfirmationModal,
  );
  return mfaModal?.options?.userRow as UserRow;
});
const internalModel = computed({
  get() {
    return props.modelValue;
  },
  set(val: boolean) {
    emit('update:modelValue', val);
  },
});

watch(
  () => props.modelValue,
  (val) => {
    if (!val) {
      resetData();
    }
  },
);

const resetData = () => {
  isSuccessful.value = false;
  hasError.value = false;
  isLoading.value = false;
};

const getFlavour = (user: UserRow) => {
  return !user.requiredMFA ? 'activation' : 'deactivation';
};

const updateTargetUserInStore = (userId: string, requiredMFA: boolean) => {
  agencyStore.setUpdatedRequiredMfaForActiveAgencyUser({ userId, requiredMFA });
};

const onClose = (user: UserRow) => {
  if (isLoading.value) return;
  // Revert state if no changes were made
  if (!isSuccessful.value) {
    updateTargetUserInStore(user.userId, user.requiredMFA);
  }
  isSuccessful.value = false;
  emit('onClose');
};

const dispatchToast = (user: UserRow) => {
  if (isSuccessful.value && !hasError.value) {
    store!.dispatch('showToast', {
      title: t(`agency.userManagement.modal.${getFlavour(user)}.successToast.title`),
      message: t(`agency.userManagement.modal.${getFlavour(user)}.successToast.message`),
      theme: ToastTheme.Success,
    });
  } else {
    store!.dispatch('showToast', {
      title: t(`agency.userManagement.modal.${getFlavour(user)}.errorToast.title`),
      message: t(`agency.userManagement.modal.${getFlavour(user)}.errorToast.message`),
      theme: ToastTheme.Danger,
    });
  }
};

const onActionButton = async (user: UserRow) => {
  isLoading.value = true;
  const newMFAValue = !user.requiredMFA;
  try {
    isSuccessful.value = await toggleMfaRequirement(user.userId, newMFAValue);
    if (!isSuccessful.value) {
      throw new Error('Failed to update MFA requirement');
    }

    if (!user.enrolledInMFA && newMFAValue) {
      await sendMFAEnrollmentEmail(user.userId, user.userInfo.email);
    }

    const isOwnUser = user.userId === userStore.auth0Id;
    if (isOwnUser && newMFAValue) {
      window.location.reload();
    }

    updateTargetUserInStore(user.userId, newMFAValue);
    dispatchToast(user);
  } catch (e) {
    hasError.value = true;
    isLoading.value = false;
    dispatchToast(user);
  }
  isLoading.value = false;

  onClose(user);
};
</script>
